// UAUCUnresolvedPanel.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// local helper (kept here so this file is standalone)
async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error)) ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

function isUnresolvedStatus(status) {
  const s = String(status || "")
    .trim()
    .toLowerCase();
  // Treat anything NOT explicitly closed/resolved/completed/done as unresolved
  return !["closed", "resolved", "completed", "done"].includes(s);
}

function matchProject(item, projectId) {
  const p = item?.project;
  const id = typeof p === "object" ? p._id : p;
  return String(id || "") === String(projectId || "");
}

function normalizeHazards(hazards) {
  if (!Array.isArray(hazards)) return "";
  return hazards
    .map((h) => h?.hazards || h?.name || h) // support refs or strings
    .filter(Boolean)
    .join(", ");
}

// Accepts "YYYY-MM-DD", "YYYY-MM-DDTHH:MM", or any Date-parsable string.
// If time is missing, clamps to start/end of day.
function parseDateTimeBoundary(input, endOfDay = false) {
  if (!input) return null;
  const s = String(input).trim();
  const hasTime = /T|\d{1,2}:\d{2}/.test(s); // "T" or "HH:MM"
  if (hasTime) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // date only -> clamp
  const d = new Date(`${s}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
}




export default function UAUCUnresolvedPanel({
  apiBase, // e.g. "https://jkumar.vercel.app"
  projectId, // selected project's _id
  onBack, // callback to return to counts view
  title = "Unresolved UAUC",
}) {
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const abortRef = useRef(null);
  // --- NEW: filters & search ---
  const [assignedToId, setAssignedToId] = useState(""); // single person filter
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [resolvedData, setResolvedData] = useState([]);
  const [ratings, setRatings] = useState({});
  const OBSERVER_BOXSHOW = ["userId", "designation", "emailId", "phone"];


  const navigate = useNavigate();

  // Optional advanced filters:
  const [advOpen, setAdvOpen] = useState(false);
  const [adv, setAdv] = useState({
    from: "", // ISO date string
    to: "", // ISO date string
    area: "", // substring match
    minRisk: "", // numeric lower bound (if you have riskValue.value)
    maxRisk: "", // numeric upper bound
  });

  // helper
  const openInNewTab = (path) => {
    const url = new URL(path, window.location.origin).toString();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // replace your openEdit with this
  const openEdit = (item) => {
    const id = item?._id || item?.id;
    const path = id ? `/edit/uauc/${id}` : `/uauc`; // fallback if no id
    openInNewTab(path);
  };
useEffect(() => {
  const ac = new AbortController();
  abortRef.current = ac;

  (async () => {
    setLoading(true);
    setErr("");
    try {
      const url = new URL(apiBase + "/uauc");
      url.searchParams.set("project", projectId);

      const res = await safeFetchJSON(url.toString(), { signal: ac.signal });
      let list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];

      // ✅ extra safety: filter by projectId
      list = list.filter((it) => {
        const proj = it?.project;
        const id = typeof proj === "object" ? proj?._id : proj;
        return String(id || "") === String(projectId);
      });

      const unresolved = list.filter((it) => isUnresolvedStatus(it?.status));
      const resolved = list.filter((it) => !isUnresolvedStatus(it?.status));

      setData(unresolved);
      setResolvedData(resolved);
      // After setData(unresolved) and setResolvedData(resolved)
      const uniqueAssignees = [
        ...new Set(list.map((it) => getAssigneeId(it)).filter(Boolean)),
      ];

      for (const assigneeId of uniqueAssignees) {
        try {
          const ratingUrl = `${apiBase}/uauc/rating/${assigneeId}`;
          const ratingRes = await safeFetchJSON(ratingUrl, {
            signal: ac.signal,
          });
          setRatings((prev) => ({ ...prev, [assigneeId]: ratingRes }));
        } catch (err) {
          console.error(
            "Rating fetch error for assignee:",
            assigneeId,
            err.message
          );
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setErr(e.message || "Failed to load UAUC.");
    } finally {
      setLoading(false);
    }
  })();

  return () => ac.abort();
}, [apiBase, projectId]);



  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) => {
      const observer = item?.observer?.name || "";
      const area = item?.area || "";
      const comment = item?.comment || "";
      const hazards = normalizeHazards(item?.hazards);
      const observation = item?.observation || "";
      return (
        observer.toLowerCase().includes(q) ||
        area.toLowerCase().includes(q) ||
        comment.toLowerCase().includes(q) ||
        observation.toLowerCase().includes(q) ||
        hazards.toLowerCase().includes(q)
      );
    });
  }, [data, query]);

  function getOpenStartDate(item) {
    return item?.openedAt || item?.date || null;
  }
  function getAssigneeId(item) {
    const a = item?.assignedTo;
    return typeof a === "object" ? a?._id : a || "";
  }
  function getAssigneeName(item) {
    const a = item?.assignedTo;
    return typeof a === "object" ? a?.name || "" : "";
  }

  function getAssigneeKey(item) {
    return getAssigneeId(item) || "__unassigned__";
  }
  function getAssigneeLabel(item) {
    return getAssigneeName(item) || "Unassigned";
  }

  const assignees = useMemo(() => {
    const map = new Map();
    for (const it of data) {
      const id = getAssigneeId(it);
      if (!id) continue;
      if (!map.has(id)) map.set(id, getAssigneeName(it) || "Unnamed");
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [data]);

  // ❗ Replace the entire `const searched = useMemo(...` block with:
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return (
      (data || [])
        // search
        .filter((item) => {
          if (!q) return true;
          const observer = (item?.observer?.name || "").toLowerCase();
          const area = (item?.area || "").toLowerCase();
          const comment = (item?.comment || "").toLowerCase();
          const observation = (item?.observation || "").toLowerCase();
          const hazards = (normalizeHazards(item?.hazards) || "").toLowerCase();
          return (
            observer.includes(q) ||
            area.includes(q) ||
            comment.includes(q) ||
            observation.includes(q) ||
            hazards.includes(q)
          );
        })
        // assignee
        .filter((item) =>
          !assignedToId
            ? true
            : String(getAssigneeId(item)) === String(assignedToId)
        )
        // advanced filters
        // advanced filters
        .filter((item) => {
          const opened = getOpenStartDate(item);
          const t = opened ? new Date(opened).getTime() : 0;

          if (adv.from) {
            const fromDt = parseDateTimeBoundary(adv.from, false);
            const fromT = fromDt ? fromDt.getTime() : 0;
            if (t && t < fromT) return false;
          }
          if (adv.to) {
            const toDt = parseDateTimeBoundary(adv.to, true);
            const toT = toDt ? toDt.getTime() : Number.POSITIVE_INFINITY;
            if (t && t > toT) return false;
          }

          if (
            adv.area &&
            !String(item?.area || "")
              .toLowerCase()
              .includes(adv.area.toLowerCase())
          ) {
            return false;
          }

          const riskVal = Number(item?.riskValue?.value ?? NaN);
          if (
            adv.minRisk &&
            !Number.isNaN(riskVal) &&
            riskVal < Number(adv.minRisk)
          )
            return false;
          if (
            adv.maxRisk &&
            !Number.isNaN(riskVal) &&
            riskVal > Number(adv.maxRisk)
          )
            return false;

          return true;
        })

        // OLDEST open first (longest open on top)
        .sort((a, b) => {
          const ta = new Date(getOpenStartDate(a) || 0).getTime();
          const tb = new Date(getOpenStartDate(b) || 0).getTime();
          return ta - tb;
        })
    );
  }, [data, query, assignedToId, adv]);

  // Group unresolved UAUC by assignee (includes "Unassigned")
  const groupedByAssignee = useMemo(() => {
    const groups = new Map();

    for (const it of filtered) {
      const key = getAssigneeKey(it);
      const name = getAssigneeLabel(it);

      if (!groups.has(key)) groups.set(key, { name, items: [] });
      groups.get(key).items.push(it);
    }

    // ensure each group's items are oldest-open-first (defensive)
    for (const g of groups.values()) {
      g.items.sort((a, b) => {
        const ta = new Date(getOpenStartDate(a) || 0).getTime();
        const tb = new Date(getOpenStartDate(b) || 0).getTime();
        return ta - tb;
      });
    }

    // order sections by name, but keep "Unassigned" first
    return Array.from(groups, ([id, g]) => ({ id, ...g })).sort((a, b) => {
      if (a.name === "Unassigned") return -1;
      if (b.name === "Unassigned") return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);

  

  function toNumberSafe(v, fallback = 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function getSlaHours(item) {
    const rv = item?.riskValue || {};
    const base = toNumberSafe(rv.alertTimeline, 0); // hours
    const escalations = Array.isArray(rv.escalationAlert)
      ? rv.escalationAlert.length
      : 0;
    const total = base * (escalations || 1); // if no escalations, use base
    return total;
  }

  function getDueAt(item) {
    const opened = getOpenStartDate(item);
    if (!opened) return null;
    const h = getSlaHours(item);
    return new Date(new Date(opened).getTime() + h * 60 * 60 * 1000);
  }

  function formatDuration(ms) {
    if (!Number.isFinite(ms)) return "—";
    const abs = Math.abs(ms);
    const days = Math.floor(abs / (24 * 3600e3));
    const hours = Math.floor((abs % (24 * 3600e3)) / 3600e3);
    if (days > 0) return `${days}d ${hours}h`;
    const mins = Math.floor((abs % 3600e3) / 60e3);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  }

  function formatDateTime(d) {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return "—";
    }
  }

  function calcAvgClosureTime(resolvedItems, assigneeId) {
    const items = resolvedItems.filter((it) => {
      const id =
        typeof it?.assignedTo === "object"
          ? it.assignedTo?._id
          : it?.assignedTo;
      return String(id || "") === String(assigneeId || "");
    });

    if (!items.length) return null;

    const durations = items
      .map((it) => {
        const opened = new Date(it?.openedAt || it?.date).getTime();
        const closed = new Date(
          it?.closedAt || it?.resolvedAt || it?.updatedAt
        ).getTime();
        return opened && closed && closed > opened ? closed - opened : null;
      })
      .filter(Boolean);

    if (!durations.length) return null;

    const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
    return formatDuration(avg);
  }

  


    const openDetails = useCallback((row) => {
      setActiveRow(row);
      setDetailsOpen(true);
    }, []);

    const closeDetails = useCallback(() => {
      setDetailsOpen(false);
      setActiveRow(null);
    }, []);

    const handleEdit = useCallback(() => {
      if (!activeRow?._id) return;
      // go to your existing edit route
      navigate(`/edit/uauc/${activeRow._id}`);
    }, [activeRow, navigate]);

    const handleDelete = useCallback(async () => {
      if (!activeRow?._id) return;
      // your delete call here; after success:
      // await api.delete(`/uauc/${activeRow._id}`)
      closeDetails();
      // optional: refetch list
    }, [activeRow, closeDetails]);

  return (
    <div>
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
            {filtered.length /* total unresolved after filters */}
          </span>
        </h4>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            className="w-56 rounded-xl border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            placeholder="Search observer, area, hazards..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="rounded-xl border border-gray-300 px-2 py-2 text-sm"
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value)}
            title="Filter by assignee"
          >
            <option value="">All Assignees</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => setAdvOpen((v) => !v)}
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              ← Back
            </button>
          )}
        </div>
      </div>
      {advOpen && (
        <div className="mb-3 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs text-gray-600">
            From (Opened Since)
            <input
              type="datetime-local" // was: date
              step="60" // minute precision
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={adv.from}
              onChange={(e) => setAdv((s) => ({ ...s, from: e.target.value }))}
            />
          </label>

          <label className="text-xs text-gray-600">
            To
            <input
              type="datetime-local" // was: date
              step="60"
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={adv.to}
              onChange={(e) => setAdv((s) => ({ ...s, to: e.target.value }))}
            />
          </label>
          <label className="text-xs text-gray-600">
            Area contains
            <input
              type="text"
              placeholder="e.g. Boiler, Yard"
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={adv.area}
              onChange={(e) => setAdv((s) => ({ ...s, area: e.target.value }))}
            />
          </label>
          <div className="flex items-end gap-2">
            <label className="flex-1 text-xs text-gray-600">
              Min Risk
              <input
                type="number"
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                value={adv.minRisk}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, minRisk: e.target.value }))
                }
              />
            </label>
            <label className="flex-1 text-xs text-gray-600">
              Max Risk
              <input
                type="number"
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                value={adv.maxRisk}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, maxRisk: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="col-span-full flex items-center gap-2">
            <button
              className="rounded-md border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100"
              onClick={() =>
                setAdv({ from: "", to: "", area: "", minRisk: "", maxRisk: "" })
              }
            >
              Clear filters
            </button>
            {(adv.from || adv.to || adv.area || adv.minRisk || adv.maxRisk) && (
              <span className="text-xs text-gray-500">Filters active</span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
          <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-xl bg-gray-200/60"
              />
            ))}
          </div>
        </div>
      ) : err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {err}
        </div>
      ) : groupedByAssignee.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          No unresolved UAUC found for this selection.
        </div>
      ) : (
        <div className="space-y-6">
          {groupedByAssignee.map((section) => (
            <div
              key={section.id}
              className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm bg-white"
            >
              {/* Section header */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between bg-gray-50 px-4 py-3 border-b rounded-t-2xl">
                {/* Left side: Assignee + details */}
                <div className="flex flex-col gap-2">
                  <h5 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    👤 {section.name}
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      {section.items.length} Open
                    </span>
                  </h5>

                  {/* 🔹 Assignee details if available */}
                  {section.items?.[0]?.assignedTo && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                      {OBSERVER_BOXSHOW.map((fieldKey) => {
                        const value = section.items[0].assignedTo[fieldKey];
                        if (!value) return null;
                        return (
                          <li
                            key={fieldKey}
                            className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1 shadow-sm border border-gray-200"
                          >
                            <span className="font-medium text-gray-500">
                              {fieldKey}:
                            </span>
                            <span className="text-gray-800 break-words">
                              {value}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Right side: Ratings */}
                {(() => {
                  const r = ratings[section.id];
                  const renderStars = (count) =>
                    "⭐".repeat(Math.max(0, count || 0));

                  if (!r) {
                    return (
                      <span className="mt-2 sm:mt-0 text-xs text-gray-400 self-end">
                        Loading rating…
                      </span>
                    );
                  }

                  return (
                    <div className="flex flex-col items-start sm:items-end text-xs text-gray-700 gap-1 bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                      <div className="flex gap-2">
                        <span className="rounded bg-green-100 px-2 py-0.5 text-green-800">
                          Resolved: {r.resolvedCount}
                        </span>
                        <span>Avg: {r.avgClosingTimeDays}d</span>
                        <span>{renderStars(r.closedStars)}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                          Open: {r.openCount}
                        </span>
                        <span>Avg: {r.avgOpenDays}d</span>
                        <span>{renderStars(r.openStars)}</span>
                      </div>
                      <div className="font-medium text-indigo-600">
                        🌟 Overall: {r.overallStars} / 5
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Section table */}
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-100">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <th className="px-4 py-2">Date (Opened)</th>
                    <th className="px-4 py-2">Opened For</th>
                    <th className="px-4 py-2">SLA Due</th>
                    <th className="px-4 py-2">Observer</th>
                    <th className="px-4 py-2">Area</th>
                    <th className="px-4 py-2">Risk</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {section.items.map((item, idx) => {
                    const opened = getOpenStartDate(item);
                    const d = opened ? new Date(opened) : null;
                    const dateStr = d ? d.toLocaleDateString() : "—";

                    const riskValue = item?.riskValue?.value || "";
                    const riskSeverity = item?.riskValue?.severity || "";
                    const risk =
                      riskValue && riskSeverity
                        ? `${riskValue} (${riskSeverity})`
                        : riskValue || riskSeverity || "—";

                    return (
                      <tr
                        key={item._id}
                        onClick={() => openEdit(item)}
                        className={`cursor-pointer hover:bg-indigo-50 ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-2">{dateStr}</td>
                        <td className="px-4 py-2">
                          {opened
                            ? formatDuration(
                                Date.now() - new Date(opened).getTime()
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-2">
                          {(() => {
                            const due = getDueAt(item);
                            if (!due) return "—";
                            const overdue = Date.now() > due.getTime();
                            return (
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  overdue
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {overdue ? "Overdue" : "On Time"}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2">
                          {item?.observer?.name || "—"}
                        </td>
                        <td className="px-4 py-2">{item?.area || "—"}</td>
                        <td className="px-4 py-2">{risk}</td>
                        <td className="px-4 py-2">
                          <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                            {item?.status || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
